import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import wsCookies from 'common/tools/ws-cookies';
import { Listing } from 'graphql/generated';
import _ from 'lodash';

import { RootState } from './root-reducer';
import { ZipCodeObject } from './search-param.slice';

interface EditListing extends Listing {
  email: string;
  phone: string;
}

interface NewListingState {
  listing: EditListing;
  cachedData?: {
    listing: EditListing;
    useEmail: boolean;
    usePhone: boolean;
  };
  useEmail: boolean;
  usePhone: boolean;
}

export interface NewListingKeySetOptions {
  year?: number;
  make?: string;
  model?: string;
}

// export const TEMP_LISTING_ID = '';
const NEW_LISTING_COOKIE = 'newListing';

const listingResetState: EditListing = {
  id: '',
  year: 0,
  make: '',
  model: '',
  trim: '',
  trimId: '',
  heading: '',
  description: '',
  mileage: 0,
  price: 0,
  zipCode: '',
  email: '',
  phone: '',
  bodyType: '',
  colorType: '',
  conditionType: '',
  cylinderType: '',
  doorType: '',
  drivetrainType: '',
  fuelType: '',
  titleStatus: '',
  transmissionType: '',
  images: [],
  listingDate: '',
  expireDate: '',
  deleteDate: '',
  status: '',
  statusDate: '',
  userId: '',
};

const initialState = (): NewListingState => {
  const listing = wsCookies.get(NEW_LISTING_COOKIE) || listingResetState;
  return {
    listing,
    useEmail: !!listing.email,
    usePhone: !!listing.phone,
  };
};

const writeListingCookie = (listing: EditListing) => {
  if (listing.id) wsCookies.set(NEW_LISTING_COOKIE, listing);
  else wsCookies.remove(NEW_LISTING_COOKIE);
};

const newListingSlice = createSlice({
  name: NEW_LISTING_COOKIE,
  initialState,
  reducers: {
    year(state, action: PayloadAction<number>) {
      state.listing.year = action.payload;
      writeListingCookie(state.listing);
    },
    make(state, action: PayloadAction<string>) {
      state.listing.make = action.payload;
      writeListingCookie(state.listing);
    },
    model(state, action: PayloadAction<string>) {
      state.listing.model = action.payload;
      writeListingCookie(state.listing);
    },
    trim(state, action: PayloadAction<string>) {
      state.listing.trim = action.payload;
      writeListingCookie(state.listing);
    },
    trimId(state, action: PayloadAction<string>) {
      state.listing.trimId = action.payload;
      writeListingCookie(state.listing);
    },
    description(state, action: PayloadAction<string>) {
      state.listing.description = action.payload;
      writeListingCookie(state.listing);
    },
    mileage(state, action: PayloadAction<number>) {
      state.listing.mileage = action.payload;
      writeListingCookie(state.listing);
    },
    price(state, action: PayloadAction<number>) {
      state.listing.price = action.payload;
      writeListingCookie(state.listing);
    },
    zipCode(state, action: PayloadAction<string>) {
      state.listing.zipCode = action.payload;
      writeListingCookie(state.listing);
    },
    email(state, action: PayloadAction<string>) {
      state.listing.email = action.payload;
      writeListingCookie(state.listing);
    },
    phone(state, action: PayloadAction<string>) {
      state.listing.phone = action.payload;
      writeListingCookie(state.listing);
    },
    useEmail(state, action: PayloadAction<boolean>) {
      state.useEmail = action.payload;
    },
    usePhone(state, action: PayloadAction<boolean>) {
      state.usePhone = action.payload;
    },
    showContactInfo(state, action: PayloadAction<boolean | undefined>) {
      state.listing.showContactInfo = action.payload;
    },
    zipCodeObject(state, action: PayloadAction<ZipCodeObject | undefined>) {
      if (action.payload) {
        state.listing.geoLocation = {
          type: 'Point',
          coordinates: [action.payload.longitude, action.payload.latitude],
        };
        state.listing.city = action.payload.city;
        state.listing.state = action.payload.state;
      } else {
        state.listing.geoLocation = undefined;
        state.listing.city = '';
        state.listing.state = '';
      }
      writeListingCookie(state.listing);
    },
    bodyType(state, action: PayloadAction<string>) {
      state.listing.bodyType = action.payload;
      writeListingCookie(state.listing);
    },
    bodyTypeOther(state, action: PayloadAction<string | undefined>) {
      state.listing.bodyTypeOther = action.payload;
      writeListingCookie(state.listing);
    },
    colorType(state, action: PayloadAction<string>) {
      state.listing.colorType = action.payload;
      writeListingCookie(state.listing);
    },
    colorTypeOther(state, action: PayloadAction<string | undefined>) {
      state.listing.colorTypeOther = action.payload;
      writeListingCookie(state.listing);
    },
    conditionType(state, action: PayloadAction<string>) {
      state.listing.conditionType = action.payload;
      writeListingCookie(state.listing);
    },
    cylinderType(state, action: PayloadAction<string>) {
      state.listing.cylinderType = action.payload;
      writeListingCookie(state.listing);
    },
    cylinderTypeOther(state, action: PayloadAction<string | undefined>) {
      state.listing.cylinderTypeOther = action.payload;
      writeListingCookie(state.listing);
    },
    doorType(state, action: PayloadAction<string>) {
      state.listing.doorType = action.payload;
      writeListingCookie(state.listing);
    },
    doorTypeOther(state, action: PayloadAction<string | undefined>) {
      state.listing.doorTypeOther = action.payload;
      writeListingCookie(state.listing);
    },
    drivetrainType(state, action: PayloadAction<string>) {
      state.listing.drivetrainType = action.payload;
      writeListingCookie(state.listing);
    },
    drivetrainTypeOther(state, action: PayloadAction<string | undefined>) {
      state.listing.drivetrainTypeOther = action.payload;
      writeListingCookie(state.listing);
    },
    fuelType(state, action: PayloadAction<string>) {
      state.listing.fuelType = action.payload;
      writeListingCookie(state.listing);
    },
    fuelTypeOther(state, action: PayloadAction<string | undefined>) {
      state.listing.fuelTypeOther = action.payload;
      writeListingCookie(state.listing);
    },
    titleStatus(state, action: PayloadAction<string>) {
      state.listing.titleStatus = action.payload;
      writeListingCookie(state.listing);
    },
    transmissionType(state, action: PayloadAction<string>) {
      state.listing.transmissionType = action.payload;
      writeListingCookie(state.listing);
    },
    transmissionTypeOther(state, action: PayloadAction<string | undefined>) {
      state.listing.transmissionTypeOther = action.payload;
      writeListingCookie(state.listing);
    },
    imageAdd(state, action: PayloadAction<{ id: string; location: string }>) {
      state.listing.images = [
        ...state.listing.images,
        {
          id: action.payload.id,
          location: action.payload.location,
          primary: state.listing.images.length ? false : true,
          order: state.listing.images.length,
          external: false,
        },
      ];
      writeListingCookie(state.listing);
    },
    imageRemove(state, action: PayloadAction<number>) {
      const newImages = state.listing.images.filter(x => x.id !== state.listing.images[action.payload].id);
      if (newImages.length > 0 && state.listing.images[action.payload].primary) newImages[0].primary = true;
      for (let i = 0; i < newImages.length; i++) newImages[i].order = i;
      state.listing.images = newImages;
      writeListingCookie(state.listing);
    },
    imageSetPrimary(state, action: PayloadAction<number>) {
      const newImages = _.clone(state.listing.images);
      const primaryImage = newImages.splice(action.payload, 1);
      primaryImage[0].primary = true;
      primaryImage[0].order = 0;
      for (let i = 0; i < newImages.length; i++) {
        newImages[i].primary = false;
        newImages[i].order = i + 1;
      }
      state.listing.images = _.orderBy([...primaryImage, ...newImages], ['order']);
      writeListingCookie(state.listing);
    },
    imageOrderDown(state, action: PayloadAction<number>) {
      if (action.payload < 2) throw new Error('invalid move down value');
      const newImages = _.clone(state.listing.images);
      newImages[action.payload - 1].order++;
      --newImages[action.payload].order;
      state.listing.images = _.orderBy(newImages, ['order']);
      writeListingCookie(state.listing);
    },
    imageOrderUp(state, action: PayloadAction<number>) {
      if (action.payload > state.listing.images.length - 2) throw new Error('invalid move up value');
      const newImages = _.clone(state.listing.images);
      --newImages[action.payload + 1].order;
      newImages[action.payload].order++;
      state.listing.images = _.orderBy(newImages, ['order']);
      writeListingCookie(state.listing);
    },
    keySet(state, action: PayloadAction<NewListingKeySetOptions>) {
      state.listing.year = 0;
      state.listing.make = '';
      state.listing.model = '';
      state.listing.heading = '';

      if (action.payload.year !== undefined) {
        state.listing.year = action.payload.year;
      }
      if (action.payload.make !== undefined) {
        state.listing.make = action.payload.make;
      }
      if (action.payload.model !== undefined) {
        state.listing.model = action.payload.model;
        state.listing.heading = `${state.listing.year.toString()} ${state.listing.make} ${state.listing.model}`;
      }
      state.listing.trimId = '';
      state.listing.trim = '';

      writeListingCookie(state.listing);
    },
    loadListing(state, action: PayloadAction<Listing>) {
      state.listing = {
        ...action.payload,
        email: action.payload.email || '',
        phone: action.payload.phone || '',
      };
      state.useEmail = !!state.listing.email;
      state.usePhone = !!state.listing.phone;
      writeListingCookie(state.listing);
    },
    createNew(state, action: PayloadAction<string>) {
      state.listing = {
        ...listingResetState,
        id: '',
        email: action.payload,
      };
      state.useEmail = true;
      state.usePhone = false;
      writeListingCookie(state.listing);
    },
    init(state) {
      state.listing = listingResetState;
      state.cachedData = undefined;
      state.useEmail = false;
      state.usePhone = false;
      writeListingCookie(state.listing);
    },
    pushCache(state) {
      state.cachedData = {
        listing: {
          ...state.listing,
        },
        useEmail: state.useEmail,
        usePhone: state.usePhone,
      };
      state.listing = listingResetState;
      state.useEmail = false;
      state.usePhone = false;
      writeListingCookie(state.listing);
    },
    popCache(state) {
      if (state.cachedData) {
        state.listing = {
          ...state.cachedData?.listing,
        };
        state.useEmail = state.cachedData.useEmail;
        state.usePhone = state.cachedData.usePhone;
      }
      state.cachedData = undefined;
      writeListingCookie(state.listing);
    },
  },
});

export const newListing = newListingSlice.reducer;
export const newListingActions = {
  ...newListingSlice.actions,
};

const rootState = (state: RootState) => state;
const newListingSliceState = createSelector(rootState, state => state.newListing);
export const newListingSelectors = {
  listing: createSelector(newListingSliceState, state => state.listing),
  id: createSelector(newListingSliceState, state => state.listing.id),
  year: createSelector(newListingSliceState, state => state.listing.year),
  make: createSelector(newListingSliceState, state => state.listing.make),
  model: createSelector(newListingSliceState, state => state.listing.model),
  trim: createSelector(newListingSliceState, state => state.listing.trim),
  trimId: createSelector(newListingSliceState, state => state.listing.trimId),
  heading: createSelector(newListingSliceState, state => state.listing.heading),
  description: createSelector(newListingSliceState, state => state.listing.description),
  mileage: createSelector(newListingSliceState, state => state.listing.mileage),
  price: createSelector(newListingSliceState, state => state.listing.price),
  city: createSelector(newListingSliceState, state => state.listing.city),
  state: createSelector(newListingSliceState, state => state.listing.state),
  zipCode: createSelector(newListingSliceState, state => state.listing.zipCode),
  email: createSelector(newListingSliceState, state => state.listing.email),
  phone: createSelector(newListingSliceState, state => state.listing.phone),
  useEmail: createSelector(newListingSliceState, state => state.useEmail),
  usePhone: createSelector(newListingSliceState, state => state.usePhone),
  showContactInfo: createSelector(newListingSliceState, state => state.listing.showContactInfo),
  geoLocation: createSelector(newListingSliceState, state => state.listing.geoLocation),
  bodyType: createSelector(newListingSliceState, state => state.listing.bodyType),
  bodyTypeOther: createSelector(newListingSliceState, state => state.listing.bodyTypeOther),
  colorType: createSelector(newListingSliceState, state => state.listing.colorType),
  colorTypeOther: createSelector(newListingSliceState, state => state.listing.colorTypeOther),
  conditionType: createSelector(newListingSliceState, state => state.listing.conditionType),
  cylinderType: createSelector(newListingSliceState, state => state.listing.cylinderType),
  cylinderTypeOther: createSelector(newListingSliceState, state => state.listing.cylinderTypeOther),
  doorType: createSelector(newListingSliceState, state => state.listing.doorType),
  doorTypeOther: createSelector(newListingSliceState, state => state.listing.doorTypeOther),
  drivetrainType: createSelector(newListingSliceState, state => state.listing.drivetrainType),
  drivetrainTypeOther: createSelector(newListingSliceState, state => state.listing.drivetrainTypeOther),
  fuelType: createSelector(newListingSliceState, state => state.listing.fuelType),
  fuelTypeOther: createSelector(newListingSliceState, state => state.listing.fuelTypeOther),
  titleStatus: createSelector(newListingSliceState, state => state.listing.titleStatus),
  transmissionType: createSelector(newListingSliceState, state => state.listing.transmissionType),
  transmissionTypeOther: createSelector(newListingSliceState, state => state.listing.transmissionTypeOther),
  images: createSelector(newListingSliceState, state => state.listing.images),
  hasCachedData: createSelector(newListingSliceState, state => !!state.cachedData),
};
